/**
 * Sprint V36 (Sa 14.09.2026) — Lucide-Icon-Mapper fuer Steckbrief-Kacheln.
 *
 * Nach Design-Spec /app/design_guidelines.json (`icon_mapping_concept`).
 * Enthaelt:
 *  - Category-Default-Icons (Kachel-Header-Icon)
 *  - Chip-Icon-Keyword-Heuristik (matches auf Substrings in Bullet-Texten)
 *  - Fallback: null -> Component rendert Dot statt Icon
 */
import {
  Activity, HeartHandshake, Wind, Compass, AlertTriangle, Zap, Rocket,
  MessageSquare, Target, Flame, UserCheck,
  Award, Eye, ShieldCheck, CheckCircle2, Maximize2, HelpingHand,
  CornerDownLeft, Brain, ShieldAlert, Lock, Hourglass,
  Anchor, Sparkles, Leaf, Handshake,
  Layers, Puzzle, Shuffle, Timer,
  Lightbulb, ThumbsUp, ThumbsDown,
} from "lucide-react";

const CATEGORY_DEFAULTS = {
  verhaltensweisen: { label: "Verhaltensweisen", icon: Activity },
  erwartungshaltung: { label: "Erwartungshaltung", icon: HeartHandshake },
  erste_impulse: { label: "Typische erste Impulse", icon: Zap },
  motivation: { label: "Motivation", icon: Rocket },
  stressmuster: { label: "Stressmuster", icon: Wind },
  werte: { label: "Ableitbare Werte", icon: Compass },
  blindspots: { label: "Blinde Flecken", icon: AlertTriangle },
  do_dont: { label: "Do & Don't", icon: Lightbulb },
  // Aliase fuer FokustypSteckbriefe.jsx / DashboardFokustypDetail.jsx
  staerken: { label: "Stärken", icon: ThumbsUp },
  schwaechen: { label: "Schwächen", icon: ThumbsDown },
  arbeitsstil: { label: "Arbeitsstil", icon: Layers },
  tueroeffner: { label: "Türöffner-Sätze", icon: MessageSquare },
  erkennungsmerkmale: { label: "Erkennungsmerkmale", icon: Eye },
  mimik: { label: "Mimik-Muster", icon: Eye },
  tonalitaet: { label: "Tonalität der Stimme", icon: MessageSquare },
};

export function getCategoryDefaults(category) {
  return CATEGORY_DEFAULTS[category] || { label: "Aspekte", icon: Puzzle };
}

export function getCategoryIconFor(category) {
  return (CATEGORY_DEFAULTS[category] || { icon: Puzzle }).icon;
}

// Chip-Icon-Heuristik: matched Substrings in Item-Texten -> Icon
// Reihenfolge = Prioritaet (spezifischer zuerst).
const CHIP_MATCHERS = {
  verhaltensweisen: [
    { pattern: /(schnell|tempo|impuls|direkt)/i, icon: Zap },
    { pattern: /(entscheid|entscheidung|kompass)/i, icon: Compass },
    { pattern: /(auftreten|k(oe|ö)rpersprache|pr(ae|ä)senz)/i, icon: UserCheck },
    { pattern: /(sprech|kommunika|rede|worte)/i, icon: MessageSquare },
    { pattern: /(pr(ae|ä)zis|fokus|detail|struktur)/i, icon: Target },
    { pattern: /(druck|stress|hitze|hektik)/i, icon: Flame },
    { pattern: /(analyse|nachdenk|reflekt)/i, icon: Brain },
    { pattern: /(harmonie|team|gruppe|beziehung)/i, icon: Handshake },
  ],
  erwartungshaltung: [
    { pattern: /(wert|anerkenn|respekt.*wertsch)/i, icon: Award },
    { pattern: /(klar|transparen|offen|ehrlich)/i, icon: Eye },
    { pattern: /(verl(ae|ä)sslich|zuverl(ae|ä)ss|kontinuit)/i, icon: ShieldCheck },
    { pattern: /(respekt|augenh(oe|ö)he)/i, icon: CheckCircle2 },
    { pattern: /(freiraum|autonomie|frei)/i, icon: Maximize2 },
    { pattern: /(unterst(ue|ü)tz|hilfe|r(ue|ü)cken)/i, icon: HelpingHand },
    { pattern: /(struktur|plan|ordnung)/i, icon: Layers },
  ],
  erste_impulse: [
    { pattern: /(los|start|anfangen|umsetz)/i, icon: Rocket },
    { pattern: /(warten|beobacht|abwarten)/i, icon: Timer },
    { pattern: /(fragen|nachfrag)/i, icon: MessageSquare },
    { pattern: /(sortieren|struktur|ordnen)/i, icon: Layers },
    { pattern: /(pr(ue|ü)f|check|analyse)/i, icon: Brain },
  ],
  motivation: [
    { pattern: /(erreich|ziel|gewinn|erfolg)/i, icon: Target },
    { pattern: /(sinn|purpose|beitrag|bewirken)/i, icon: Sparkles },
    { pattern: /(beziehung|zusammen|verbund)/i, icon: Handshake },
    { pattern: /(sicherheit|schutz|stabilit)/i, icon: ShieldCheck },
    { pattern: /(wachstum|entwickl|lernen)/i, icon: Leaf },
    { pattern: /(kontrolle|(ue|ü)berblick|steuer)/i, icon: Compass },
  ],
  stressmuster: [
    { pattern: /(r(ue|ü)ckzug|abschot|zur(ue|ü)ck)/i, icon: CornerDownLeft },
    { pattern: /(angriff|angreif|dominan|attacke)/i, icon: AlertTriangle },
    { pattern: /(analysier|(ue|ü)berdenk|gr(ue|ü)bel)/i, icon: Brain },
    { pattern: /(harmon|gefall|anpass)/i, icon: ShieldAlert },
    { pattern: /(kontroll.*verlust|starr|festhalt)/i, icon: Lock },
    { pattern: /(hektisch|schnell|zeit)/i, icon: Hourglass },
    { pattern: /(erstarr|blockade|stille)/i, icon: Anchor },
  ],
  werte: [
    { pattern: /(freiheit|autonomie|selbstbestim)/i, icon: Maximize2 },
    { pattern: /(wahrheit|ehrlich|klarheit|pr(ae|ä)zision)/i, icon: Eye },
    { pattern: /(vertrauen|verbund|zugeh(oe|ö)r)/i, icon: HeartHandshake },
    { pattern: /(sicherheit|stabilit(ae|ä)t|verl(ae|ä)ss)/i, icon: ShieldCheck },
    { pattern: /(wachstum|entwickl|potenzial)/i, icon: Leaf },
    { pattern: /(leistung|exzellen|qualit)/i, icon: Award },
    { pattern: /(anerken|respekt|w(ue|ü)rde)/i, icon: CheckCircle2 },
  ],
  blindspots: [
    { pattern: /(vorschnell|impuls|(ue|ü)bereil)/i, icon: Zap },
    { pattern: /(paralys|(ue|ü)berdenk|blockade)/i, icon: Brain },
    { pattern: /(unterordn|klein|zur(ue|ü)cksteck)/i, icon: CornerDownLeft },
    { pattern: /(kontroll|festhalt|starr)/i, icon: Lock },
    { pattern: /(perfekt|detail.*versink)/i, icon: Target },
    { pattern: /(harmoniesucht|konflikt.*meid)/i, icon: ShieldAlert },
  ],
  do_dont: [
    { pattern: /(kurz|klar|direkt|punkt)/i, icon: Target },
    { pattern: /(daten|fakten|zahlen|beleg)/i, icon: Layers },
    { pattern: /(zeit|geduld|ruhe)/i, icon: Timer },
    { pattern: /(persoenlich|verbind|beziehung)/i, icon: Handshake },
  ],
};

/**
 * Bestimmt ein passendes Chip-Icon fuer ein Item innerhalb einer Kategorie.
 * @returns {React.ComponentType|null}
 */
export function getChipIconFor(category, text) {
  const list = CHIP_MATCHERS[category];
  if (!list) return null;
  const t = String(text || "");
  for (const { pattern, icon } of list) {
    if (pattern.test(t)) return icon;
  }
  return null;
}
